/* @flow */
import * as React from "react";
import { apiCall } from "../common/fetch";
import * as Types from "./NextGenChartTypes";
import * as Helpers from "./NextGenChartMath";

import type { Profile } from "../../redux/CommonTypes";

type Data = Array<Types.Point> | "void" | "darkness";

export async function fetchSection<S: Types.Section>(
  section: S,
  profile: Profile
): Promise<{
  section: S,
  data: Data
}> {
  if (section.interval === 1000) {
    return {
      section,
      data: "darkness"
    };
  }

  const interval = section.interval / 1000;
  const symbol = section.stock;

  const [start, end] = Helpers.getSectionBoundaries(section).map(b => b / 1000);

  const response = await apiCall(
    `/api/stocks/history/csv?symbol=${symbol}&interval=${interval}&start=${start}&end=${end}&user_id=${
      profile.id
    }`
  );

  if (response.status !== 200) {
    if (response.status === 403) {
      return {
        section,
        data: "darkness"
      };
    }

    throw new TypeError(
      `Failed to load the history for ${symbol} from ${new Date(
        start * 1000
      ).toString()} to ${new Date(
        end * 1000
      ).toString()} due to HTTP status code mismatch (${response.status})`
    );
  }

  const text = await response.text();
  const lines = text.split("\n");
  const data = [];

  for (const line of lines) {
    if (line.length > 0) {
      const [epoch, open, close, min, max] = line.split(",");

      data.push({
        epoch: parseInt(epoch, 10),
        open: parseFloat(open),
        close: parseFloat(close),
        min: parseFloat(min),
        max: parseFloat(max)
      });
    }
  }

  if (data.length === 0) {
    return {
      section,
      data: "void"
    };
  }

  return {
    section,
    data
  };
}

// Компонент, который загружает данные для
// секции. В зависимости от типа (прошлое,
// настоящее или будущее) может по-разному
// относиться к кешированию и обновлению.

type SectionDataProps = {
  section: Types.Section,
  type: Types.SectionType,
  cache: Array<Types.Point> | null,
  onData: ({ section: Types.Section, data: Data }) => void,
  profile: Profile
};

class SectionData extends React.Component<SectionDataProps> {
  timer: ?TimeoutID = null;

  fetch = async () => {
    let data = null;

    try {
      data = await this.props.fetchSection(
        this.props.section,
        this.props.profile
      );
    } catch (error) {
      // Если data == null, значит произошла ошибка в запросе.
      // Я не сунул onData() в try-catch, потому что этим мы бы
      // поймали ошибки, которые ловить не стоит.
      if (process.env.NODE_ENV === "development") {
        console.error(error);
      }
    }

    if (data != null) {
      this.props.onData(data);
    }
  };

  componentDidMount() {
    // Появление компонента вызывает запрос.
    // Чтобы не делать миллион запросов при
    // зуме/перемещении по истории, запрос
    // происходит по будильнику, который мы
    // отменим, если данные больше не нужны.
    const { type, cache } = this.props;

    if (type === "present" || (type === "past" && cache == null)) {
      this.timer = setTimeout(this.fetch, 200);
    }
  }

  componentDidUpdate(prevProps: SectionDataProps) {
    if (prevProps.type === "present" && this.props.type === "past") {
      if (this.timer != null) {
        clearTimeout(this.timer);
        this.timer = null;
      }

      this.fetch();
    }
  }

  componentWillUnmount() {
    if (this.timer != null) {
      clearTimeout(this.timer);
    }
  }

  render() {
    return null;
  }
}

export default SectionData;



// WEBPACK FOOTER //
// ./src/components/Chart/SectionData.js